import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Confirm from './Confirm';
import Toast from './Toast';
import CaseStudyEditor from './CaseStudyEditor';

function SortableItem({ cs, idx, onClick, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: cs._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="sortable-item"
      {...attributes}
      {...listeners}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '16px',
        backgroundColor: isDragging ? 'rgba(244,241,234,0.1)' : 'rgba(244,241,234,0.02)',
        border: '1px solid rgba(244,241,234,0.08)',
        borderRadius: '2px',
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      onClick={(e) => {
        if (e.target.closest('button') || e.target.closest('[role="button"]')) return;
      }}
      >
        <div style={{
          fontSize: '13px',
          color: 'rgba(244,241,234,0.4)',
          fontFamily: 'var(--mono)',
          minWidth: '24px',
          paddingRight: '8px',
          borderRight: '1px solid rgba(244,241,234,0.1)'
        }}>
          {idx + 1}
        </div>
        <div style={{ flex: 1, cursor: 'pointer' }} onClick={onClick}>
          <div style={{ fontWeight: 600 }}>{cs.title}</div>
          <div style={{ fontSize: '13px', color: 'rgba(244,241,234,0.6)' }}>{cs.client}</div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ fontSize: '11px', color: 'var(--graphite-2)', whiteSpace: 'nowrap' }}>
            {cs.featured && <span style={{ marginRight: '8px' }}>★ FEATURED</span>}
            <span style={{ color: cs.status === 'published' ? '#6FCF97' : 'var(--graphite-2)' }}>
              {cs.status === 'published' ? '✓ LIVE' : 'DRAFT'}
            </span>
          </div>
          <button
            onClick={onDelete}
            style={{ background: 'none', border: 'none', color: 'var(--graphite-2)', cursor: 'pointer', fontSize: '14px', padding: '0 8px', pointerEvents: 'auto' }}
          >
            ⋯
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CaseStudies() {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [dragMode, setDragMode] = useState(false);
  const [displayMode, setDisplayMode] = useState(false);
  const [selectedForDisplay, setSelectedForDisplay] = useState(new Set());

  const sensors = useSensors(
    useSensor(PointerSensor, {
      distance: 8,
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchCaseStudies();
  }, []);

  const fetchCaseStudies = async () => {
    try {
      const response = await fetch('/api/case-studies');
      const data = await response.json();
      const sorted = data.sort((a, b) => (a.order || 0) - (b.order || 0));
      setCaseStudies(sorted);
      setLoading(false);
    } catch (err) {
      showToast('Error loading case studies', 'error');
      setLoading(false);
    }
  };

  const showToast = (msg, type = 'default') => {
    setToast({ message: msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`/api/case-studies/${deleteId}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Delete failed');

      showToast('Case study deleted');
      setConfirm(null);
      setDeleteId(null);
      fetchCaseStudies();
    } catch (err) {
      showToast('Error deleting case study', 'error');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = caseStudies.findIndex(cs => cs._id === active.id);
    const newIndex = caseStudies.findIndex(cs => cs._id === over.id);

    const reordered = arrayMove(caseStudies, oldIndex, newIndex);
    setCaseStudies(reordered);

    try {
      const updatePromises = reordered.map((cs, idx) => {
        const csData = { ...cs };
        delete csData._id;
        delete csData.__v;
        return fetch(`/api/case-studies/${cs._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...csData, order: idx })
        });
      });

      await Promise.all(updatePromises);
      showToast('Order updated');
    } catch (err) {
      showToast('Error updating order', 'error');
      fetchCaseStudies();
    }
  };

  const getFilteredStudies = () => {
    if (filter === 'all') return caseStudies;
    return caseStudies.filter(cs => cs.status === filter || (filter === 'featured' && cs.featured));
  };

  const toggleSelection = (id) => {
    const newSelected = new Set(selectedForDisplay);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedForDisplay(newSelected);
  };

  const saveDisplaySelection = async () => {
    try {
      const selectedIds = Array.from(selectedForDisplay);
      const response = await fetch('/api/settings/case-studies-display', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayedIds: selectedIds })
      });
      if (!response.ok) throw new Error('Save failed');
      showToast('Display selection saved');
      setDisplayMode(false);
    } catch (err) {
      showToast('Error saving display selection', 'error');
    }
  };

  const getDisplayedStudies = () => {
    if (selectedForDisplay.size === 0) return caseStudies;
    return caseStudies.filter(cs => selectedForDisplay.has(cs._id));
  };

  const filtered = getFilteredStudies();
  const stats = {
    total: caseStudies.length,
    featured: caseStudies.filter(cs => cs.featured).length,
    published: caseStudies.filter(cs => cs.status === 'published').length
  };

  if (loading) {
    return <div className="admin-page"><p>Loading...</p></div>;
  }

  if (editingId) {
    return (
      <CaseStudyEditor
        caseStudyId={editingId === 'new' ? null : editingId}
        onBack={() => {
          setEditingId(null);
          fetchCaseStudies();
        }}
      />
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-page__head">
        <div>
          <h1 className="admin-page__title">Case studies</h1>
          <div className="admin-page__sub">
            <span><strong>{stats.total}</strong> total</span>
            <span><strong>{stats.published}</strong> published</span>
            <span><strong>{stats.featured}</strong> featured</span>
          </div>
        </div>
        <div className="admin-page__actions">
          <button className="btn-primary" onClick={() => setEditingId('new')}>+ New case study</button>
        </div>
      </div>

      <div className="admin-toolbar">
        <div className="admin-tabs">
          {['all', 'published', 'draft'].map((tab) => (
            <button
              key={tab}
              className={`admin-tab ${filter === tab ? 'is-active' : ''}`}
              onClick={() => setFilter(tab)}
            >
              {tab === 'all' && 'All'}
              {tab === 'published' && '✓ Published (shown on landing)'}
              {tab === 'draft' && 'Draft (hidden)'}
            </button>
          ))}
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={filtered.map(cs => cs._id)} strategy={verticalListSortingStrategy}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '32px' }}>
            {filtered.map((cs, idx) => (
              <SortableItem
                key={cs._id}
                cs={cs}
                idx={idx}
                onClick={() => setEditingId(cs._id)}
                onDelete={(e) => {
                  e.stopPropagation();
                  setDeleteId(cs._id);
                  setConfirm({
                    title: `Delete "${cs.title}"?`,
                    onConfirm: handleDelete,
                    onCancel: () => {
                      setConfirm(null);
                      setDeleteId(null);
                    }
                  });
                }}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {confirm && (
        <Confirm
          title={confirm.title}
          body=""
          onConfirm={confirm.onConfirm}
          onCancel={confirm.onCancel}
          confirmLabel="Delete"
          danger
        />
      )}
      {toast && <Toast message={toast.message} type={toast.type} />}
    </div>
  );
}
